import { useUpdateCollectionMediaMutation } from '@/lib/services/collections/collections.query'
import { useCallback, useMemo, useRef, useState } from 'react'

export const EMPTY_COLLECTION_MEDIA_DRAFTS = Object.freeze({})

export const getCollectionMediaDraftKey = (collectionId) => {
  const normalizedId = Number(collectionId)

  return Number.isFinite(normalizedId) && normalizedId > 0 ? String(normalizedId) : null
}

export const getCollectionMediaId = (media) => {
  const mediaId = Number(media?.id)

  return Number.isFinite(mediaId) && mediaId > 0 ? mediaId : null
}

const getOptionalNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const number = Number(value)

  return Number.isFinite(number) ? number : null
}

export const getCollectionMediaPayload = (media) => {
  const id = getCollectionMediaId(media)

  if (id === null) {
    return null
  }

  const focalPointX = getOptionalNumber(media?.focal_point_x)
  const focalPointY = getOptionalNumber(media?.focal_point_y)

  return {
    id,
    ...(focalPointX !== null ? { focal_point_x: focalPointX } : {}),
    ...(focalPointY !== null ? { focal_point_y: focalPointY } : {}),
  }
}

const collectionMediaPayloadsMatch = (first, second) =>
  JSON.stringify(first) === JSON.stringify(second)

export const getCollectionPrimaryMedia = (collection) =>
  Array.isArray(collection?.media) ? (collection.media[0] ?? null) : null

export const hasCollectionMediaDraft = (collectionMediaDrafts, collectionId) => {
  const draftKey = getCollectionMediaDraftKey(collectionId)

  return (
    draftKey !== null && Object.prototype.hasOwnProperty.call(collectionMediaDrafts ?? {}, draftKey)
  )
}

export const getCollectionMediaWithDraft = (collection, collectionMediaDrafts = {}) => {
  const draftKey = getCollectionMediaDraftKey(collection?.id)

  if (
    draftKey !== null &&
    Object.prototype.hasOwnProperty.call(collectionMediaDrafts ?? {}, draftKey)
  ) {
    return collectionMediaDrafts[draftKey]?.media ?? null
  }

  return getCollectionPrimaryMedia(collection)
}

export const applyCollectionMediaDrafts = (collections = [], collectionMediaDrafts = {}) => {
  if (!Array.isArray(collections) || Object.keys(collectionMediaDrafts ?? {}).length === 0) {
    return collections
  }

  return collections.map((collection) => {
    const draftKey = getCollectionMediaDraftKey(collection?.id)

    if (
      draftKey === null ||
      !Object.prototype.hasOwnProperty.call(collectionMediaDrafts, draftKey)
    ) {
      return collection
    }

    const draftMedia = collectionMediaDrafts[draftKey]?.media ?? null

    return {
      ...collection,
      media: draftMedia ? [draftMedia] : [],
    }
  })
}

const markCollectionMediaSaveError = ({ error, savedCount }) => {
  const collectionMediaError =
    error instanceof Error ? error : new Error('Collection image save failed')

  collectionMediaError.collectionMediaSaveFailed = true
  collectionMediaError.collectionMediaPartialFailure = savedCount > 0

  return collectionMediaError
}

export function useCollectionMediaDrafts() {
  const updateCollectionMediaMutation = useUpdateCollectionMediaMutation()
  const [drafts, setDrafts] = useState({})
  const [isSavingDrafts, setIsSavingDrafts] = useState(false)
  const draftsRef = useRef(drafts)
  const setDraftsAndRef = useCallback((updater) => {
    const nextDrafts = typeof updater === 'function' ? updater(draftsRef.current) : updater

    draftsRef.current = nextDrafts
    setDrafts(nextDrafts)
  }, [])
  const clearDrafts = useCallback(() => {
    setDraftsAndRef({})
  }, [setDraftsAndRef])
  const clearDraft = useCallback(
    (collectionId) => {
      const draftKey = getCollectionMediaDraftKey(collectionId)

      if (draftKey === null) {
        return
      }

      setDraftsAndRef((currentDrafts) => {
        if (!Object.prototype.hasOwnProperty.call(currentDrafts, draftKey)) {
          return currentDrafts
        }

        const nextDrafts = { ...currentDrafts }
        delete nextDrafts[draftKey]

        return nextDrafts
      })
    },
    [setDraftsAndRef]
  )
  const stageDraft = useCallback(
    (collection, nextMedia) => {
      const draftKey = getCollectionMediaDraftKey(collection?.id)

      if (draftKey === null) {
        return
      }

      setDraftsAndRef((currentDrafts) => {
        const nextDrafts = { ...currentDrafts }
        const existingDraft = currentDrafts[draftKey]
        const originalMediaPayload =
          existingDraft?.originalMediaPayload ??
          getCollectionMediaPayload(getCollectionPrimaryMedia(collection))
        const nextMediaPayload = getCollectionMediaPayload(nextMedia)

        if (collectionMediaPayloadsMatch(originalMediaPayload, nextMediaPayload)) {
          delete nextDrafts[draftKey]
          return nextDrafts
        }

        nextDrafts[draftKey] = {
          collectionId: Number(draftKey),
          originalMediaPayload,
          media: nextMedia ?? null,
        }

        return nextDrafts
      })
    },
    [setDraftsAndRef]
  )
  const saveDrafts = useCallback(async () => {
    const draftEntries = Object.values(draftsRef.current ?? {})
    let savedCount = 0

    if (draftEntries.length === 0) {
      return { savedCount }
    }

    setIsSavingDrafts(true)

    try {
      for (const draft of draftEntries) {
        const mediaPayload = getCollectionMediaPayload(draft?.media)

        try {
          await updateCollectionMediaMutation.mutateAsync({
            collectionId: draft.collectionId,
            media: mediaPayload ? [mediaPayload] : [],
          })
          savedCount += 1
          clearDraft(draft.collectionId)
        } catch (error) {
          throw markCollectionMediaSaveError({ error, savedCount })
        }
      }

      return { savedCount }
    } finally {
      setIsSavingDrafts(false)
    }
  }, [clearDraft, updateCollectionMediaMutation])
  const hasCurrentDrafts = useCallback(() => Object.keys(draftsRef.current ?? {}).length > 0, [])

  return useMemo(
    () => ({
      drafts,
      hasDrafts: Object.keys(drafts).length > 0,
      isSavePending: isSavingDrafts || updateCollectionMediaMutation.isPending,
      stageDraft,
      clearDrafts,
      saveDrafts,
      hasCurrentDrafts,
    }),
    [
      clearDrafts,
      drafts,
      hasCurrentDrafts,
      isSavingDrafts,
      saveDrafts,
      stageDraft,
      updateCollectionMediaMutation.isPending,
    ]
  )
}
